// Brain Simulations — normalização do saldo de créditos (única fonte dos medidores).
//
// O payload cru de `GET /subscriptions/my-usage` continua guardado como hoje
// (store: `aggregatedUsage`); aqui ele é traduzido para uma forma agnóstica de
// moeda, para que cada superfície de saldo (Header, AppSidebar, modal de upload,
// Settings) bifurque por `billing_model` num só lugar:
//   - `unified`   → 1 medidor "Brain Simulations" (custo por mídia vem de `brain_simulation_costs`);
//   - `per_media` → os 2 medidores atuais (imagens/vídeos), intocados.
//
// O custo do Brain Simulation NUNCA é hardcoded no front: `costFor()` lê `brain_simulation_costs`
// do payload (contrato do backend), por isso vale 0 se o campo não vier.
//
// SYN-2191: o custo do vídeo passa a depender da DURAÇÃO (faixa curta ≤ `short_video_max_seconds`
// custa `brain_simulation_short_video_cost`; acima disso, `brain_simulation_costs.video`) e o plano
// ganha um limite de duração (`max_video_duration_seconds`, `null` = legado/sem limite de plano).
// Os três campos são aditivos: API sem eles → comportamento de hoje (vídeo pelo custo cheio, sem
// limite de plano). Nada de 3/5/60 hardcoded aqui.
import { computed, unref } from 'vue';
import { useUserStore } from '@/stores/user';
import { minutesOf } from '@/constants/videoDuration';

export const BILLING_MODEL_UNIFIED = 'unified';
export const BILLING_MODEL_PER_MEDIA = 'per_media';

const toNumber = (value) => Number(value) || 0;

// SYN-2761: datas do `/my-usage` (ISO) → Date; ausente ou inválida → null.
const toDateOrNull = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const remainingOf = (used, allowance) => Math.max(0, toNumber(allowance) - toNumber(used));

const remainingPercentOf = (used, allowance) => {
    const safeAllowance = toNumber(allowance);
    if (safeAllowance <= 0) return 0;
    return (remainingOf(used, allowance) / safeAllowance) * 100;
};

// Mesmos limiares do Header de hoje (80% aviso / 100% esgotado).
const colorOf = (used, allowance) => {
    const safeAllowance = toNumber(allowance);
    if (safeAllowance === 0) return 'grey';
    const consumed = (toNumber(used) / safeAllowance) * 100;
    if (consumed >= 100) return 'error';
    if (consumed >= 80) return 'warning';
    return 'success';
};

const buildMeter = (key, used, allowance) => ({
    key,
    used: toNumber(used),
    allowance: toNumber(allowance),
    remaining: remainingOf(used, allowance),
    remainingPercent: remainingPercentOf(used, allowance),
    color: colorOf(used, allowance),
});

/**
 * @param {import('vue').Ref<object>|object} [usageSource] payload de
 *   `aggregated_usage` já buscado pelo chamador (modal de upload e tela de
 *   Assinatura têm o seu próprio fetch). Omitido → usa o do store do usuário.
 * @returns {{
 *   billingModel: import('vue').ComputedRef<'unified'|'per_media'>,
 *   isUnified: import('vue').ComputedRef<boolean>,
 *   meters: import('vue').ComputedRef<Array<object>>,
 *   baseMeter: import('vue').ComputedRef<object|null>,
 *   bonusMeter: import('vue').ComputedRef<object|null>,
 *   baseRenewsAt: import('vue').ComputedRef<Date|null>,
 *   bonusExpiresAt: import('vue').ComputedRef<Date|null>,
 *   brainSimulationCosts: import('vue').ComputedRef<object>,
 *   costFor: (mediaType: string, durationSeconds?: number) => number,
 *   isShortVideo: (durationSeconds?: number) => boolean,
 *   maxVideoDurationSeconds: import('vue').ComputedRef<number|null>,
 *   planVideoDurationMinutes: import('vue').ComputedRef<number|null>,
 *   shortVideoMaxSeconds: import('vue').ComputedRef<number>,
 *   brainSimulationShortVideoCost: import('vue').ComputedRef<number>,
 *   isLoading: import('vue').ComputedRef<boolean>,
 *   error: import('vue').ComputedRef<boolean>,
 * }} `isLoading`/`error` só têm valor quando a fonte é o store; com
 *   `usageSource` explícito o dono do fetch controla os próprios estados.
 */
export function useCreditUsage(usageSource) {
    const store = usageSource ? null : useUserStore();
    const usage = computed(() => (usageSource ? unref(usageSource) : store.aggregatedUsage));

    const billingModel = computed(() =>
        usage.value?.billing_model === BILLING_MODEL_UNIFIED
            ? BILLING_MODEL_UNIFIED
            : BILLING_MODEL_PER_MEDIA
    );

    const isUnified = computed(() => billingModel.value === BILLING_MODEL_UNIFIED);

    const meters = computed(() => {
        const data = usage.value;
        if (!data) return [];
        if (isUnified.value) {
            return [buildMeter('credits', data.total_credits_used, data.total_credits_allowance)];
        }
        return [
            buildMeter('images', data.total_images_used, data.total_images_allowance),
            buildMeter('videos', data.total_videos_used, data.total_videos_allowance),
        ];
    });

    // SYN-2761: o total de Brain Simulations partido em "Seus créditos" (plano + pacotes) e
    // "Bônus" (cortesia). Só unified — o legado por mídia não tem esse recorte. `meters`
    // continua com o total único (o Header lê `meters[0]`).
    const baseMeter = computed(() => {
        const data = usage.value;
        if (!data || !isUnified.value) return null;
        return buildMeter(
            'base',
            Math.max(0, toNumber(data.total_credits_used) - toNumber(data.bonus_credits_used)),
            Math.max(0, toNumber(data.total_credits_allowance) - toNumber(data.bonus_credits_allowance)),
        );
    });

    // A barra de bônus só existe com saldo de bônus (bônus gasto inteiro some, mesmo sem vencer).
    const bonusMeter = computed(() => {
        const data = usage.value;
        if (!data || !isUnified.value) return null;
        const meter = buildMeter('bonus', data.bonus_credits_used, data.bonus_credits_allowance);
        return meter.remaining > 0 ? meter : null;
    });

    // `null` = sem renovação a mostrar / bônus sem vencimento.
    const baseRenewsAt = computed(() => toDateOrNull(usage.value?.base_credits_renews_at));
    const bonusExpiresAt = computed(() => toDateOrNull(usage.value?.bonus_credits_expires_at));

    const brainSimulationCosts = computed(() => usage.value?.brain_simulation_costs ?? {});

    // Limite de duração do plano do cliente (SYN-2191 RN4/RN12). `null` = legado, cliente sem
    // plano unified vigente, ou API anterior ao contrato — nenhum gate por plano.
    const maxVideoDurationSeconds = computed(() => usage.value?.max_video_duration_seconds ?? null);
    // Fronteira da faixa curta e o custo dela. Ausentes (API velha) → 0, e aí `costFor` cai
    // no custo cheio do `brain_simulation_costs`, que é o comportamento de hoje.
    const shortVideoMaxSeconds = computed(() => toNumber(usage.value?.short_video_max_seconds));
    const brainSimulationShortVideoCost = computed(() => toNumber(usage.value?.brain_simulation_short_video_cost));

    // ÚNICO predicado de faixa curta (não reimplementar em componente): exige as duas pontas
    // do contrato (fronteira E preço) e uma duração medida. Fronteira ESTRITA (RN1/D3):
    // `duration <= short_video_max_seconds` é curto; qualquer buraco cai na faixa cara —
    // nunca sub-estimar o débito.
    const isShortVideo = (durationSeconds) =>
        shortVideoMaxSeconds.value > 0 &&
        brainSimulationShortVideoCost.value > 0 &&
        Number.isFinite(durationSeconds) &&
        durationSeconds <= shortVideoMaxSeconds.value;

    const costFor = (mediaType, durationSeconds) =>
        (mediaType === 'video' && isShortVideo(durationSeconds)
            ? brainSimulationShortVideoCost.value
            : toNumber(brainSimulationCosts.value[mediaType]));

    // Porta ÚNICA das superfícies de copy que dizem "N min" (dica do estado vazio, painel de
    // Assinatura, Central de Ajuda): já carrega a guarda de moeda e a de valor utilizável, para
    // a mesma regra não ser reescrita em cada tela. `null` = não afirmar limite nenhum.
    const planVideoDurationMinutes = computed(() =>
        isUnified.value && Number.isFinite(maxVideoDurationSeconds.value) && maxVideoDurationSeconds.value > 0
            ? minutesOf(maxVideoDurationSeconds.value)
            : null
    );

    const isLoading = computed(() => (store ? store.isLoadingUsage : false));
    const error = computed(() => (store ? store.usageError : false));

    return {
        billingModel,
        isUnified,
        meters,
        baseMeter,
        bonusMeter,
        baseRenewsAt,
        bonusExpiresAt,
        brainSimulationCosts,
        costFor,
        isShortVideo,
        maxVideoDurationSeconds,
        planVideoDurationMinutes,
        shortVideoMaxSeconds,
        brainSimulationShortVideoCost,
        isLoading,
        error,
    };
}
